import React, { FC, useState } from 'react'
import cx from 'classnames'
import { useNavigate, Link } from 'react-router-dom'
import { Geometry } from 'models/map'
import { getBoundsFromGeometry } from 'utils/coordinates'
import { Project, Area } from 'models/project'
import { Heading } from 'components/Heading'
import { AreaTypeDetails } from 'components/AreaTypeDetails'
import { Stack } from 'components/Stack'
import { Icon } from 'components/Icon'
import { ProjectNameDialog } from '../ProjectNameDialog'
import { useTranslation } from 'react-i18next'
import { useFetchCompareAreas, useUpdateProject } from 'services/project'
import { Flex } from 'components/Flex'
import { Badge } from 'components/Badge'
import { Text } from 'components/Text'
import { getAreaIcon } from 'utils/areaDetails'
import { GenerateReport } from './components/GenerateReport'
import { CompareMenu } from './components/CompareMenu'
import styles from './ProjectInfo.module.scss'
import { Events, FlyToBounds } from 'constants/events'
import { fire } from 'utils/customEvent'
import { Button } from 'components/Button'
import { Dialog } from 'components/Dialog'
import { RootStateOrAny, useSelector, useDispatch } from 'react-redux'
import { deleteAreaAndRefetch, deleteArea } from 'actions/project'

type ProjectInfoProps = {
  project: Project
  area?: Area
  full: boolean
  showCompareMenu?: boolean
}

export const ProjectInfo: FC<ProjectInfoProps> = ({
  project,
  area,
  full,
  showCompareMenu = false,
}) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [updateProject] = useUpdateProject(project.id)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const areaData = useSelector((state: RootStateOrAny) => state.area)

  let { areas = [] } = useFetchCompareAreas(project.id)

  let arrArea: Area[] = areas

  const flyToArea = (geometry: Geometry) => {
    const bounds = getBoundsFromGeometry(geometry)
    fire<FlyToBounds>(Events.FlyToBounds, { bounds })
  }

  const handleDeleteAreaClose = () => {
    dispatch(deleteArea)
  }

  function BorrarArea() {
    arrArea = []

    for (var i = 0; i < areas.length; i++) {
      if (area !== areas[i]) {
        arrArea.push(areas[i])
      }
    }

    var url = '/projects/' + project.id
    navigate(url)
  }

  const handleDeleteArea = () => {
    dispatch(deleteAreaAndRefetch(project.id, areaData.area))
    dispatch(deleteArea)
    BorrarArea()
  }

  return (
    <div className={styles.projectInfo}>
      <Stack space="small">
        <Flex align="center">
          <Heading
            className={cx(styles.title, full ? styles.full : null)}
            size="secondary"
            truncate
          >
            {project.name}
          </Heading>
          <button
            aria-label={t('project_info.edit_name')}
            className={styles.editButton}
            onClick={() => setDialogOpen(true)}
          >
            <Icon name="edit-2-line" />
          </button>
        </Flex>
        <Flex align="center" justify="between">
          <Flex
            gap="sm"
            align="center"
            className={cx(styles.compareWrapper, showCompareMenu ? styles.full : null)}
          >
            <button onClick={() => flyToArea(project.geometry)} className={styles.areaButton}>
              <Badge color={styles.primary}>
                <AreaTypeDetails project={project} />
              </Badge>
            </button>
            {area && (
              <>
                <Icon name="add-line" />
                <button onClick={() => flyToArea(area.geometry)} className={styles.areaButton}>
                  <Badge color={area.color}>
                    <Flex gap="sm" align="center" className={styles.compareArea}>
                      <Icon name={getAreaIcon(area.areaType)} />
                      <Text component="span" size="small" weight="strong" truncate>
                        {area.name}
                      </Text>
                      <Link
                        to={`/projects/${project.id}`}
                        className={styles.closeArea}
                        aria-label={t('project_info.compare.close')}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Icon name="close-line" />
                      </Link>
                    </Flex>
                  </Badge>
                </button>
              </>
            )}

            {showCompareMenu && <CompareMenu handleDeleteArea={BorrarArea} areas={arrArea} />}
          </Flex>
        </Flex>
        {showCompareMenu && <GenerateReport project={project} area={area} />}
      </Stack>

      <ProjectNameDialog
        isOpen={dialogOpen}
        name={project.name}
        onClose={() => setDialogOpen(false)}
        onConfirm={updateProject}
      />

      <Dialog
        isOpen={areaData.isOpen}
        onClose={handleDeleteAreaClose}
        aria-label={t('project_info.dialog.title')}
      >
        <Stack space="medium">
          <Stack space="small">
            <Heading>{t('project_info.dialog.title')}</Heading>
            <Text>{t('project_info.dialog.cta')}</Text>
          </Stack>
          <Stack space="medium">
            <Button onClick={handleDeleteAreaClose} loading={false} variant="secondary" full>
              {t('project_info.dialog.cancel')}
            </Button>
            <Button onClick={handleDeleteArea} loading={false} variant="danger" full>
              {t('project_info.dialog.confirm')}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </div>
  )
}

ProjectInfo.displayName = 'ProjectInfo'
